import React from "react";
import {IPlanMatrix} from "../../../../../../../../declerations/server/payment_plan_models";
import {FormControl, Select, MenuItem, Box} from "@mui/material";
import {PlanButton} from "./PlanButton";
import {Colors, Container, Text} from "bounce-ui/backoffice";
import {multiply} from "@bounce/shared/helpers";
import {PaymentPlanBuilderFrequency, TableCellStyle} from "../utils";

const DISCOUNT_LABELS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const NUMBER_OF_INSTALLMENTS_LABELS = [1, 2, 3, 6, 9, 12, 18, 24, 30, 36, 42, 48, 54, 60];

interface IPaymentTable {
  planOffers: (IPlanMatrix | null)[][];
  selectedPlan: IPlanMatrix | null;
  setSelectedPlan: (selectedPlan: IPlanMatrix) => void;
  frequency: PaymentPlanBuilderFrequency;
}

const getActualInstallments = (baseInstallments: number, frequency: PaymentPlanBuilderFrequency) => {
  if (baseInstallments === 1) return 1;
  switch (frequency) {
    case "weekly":
      return baseInstallments * 4;
    case "bi_weekly":
    case "first_and_fifteenth":
      return baseInstallments * 2;
    case "monthly":
    default:
      return baseInstallments;
  }
};

export const PaymentTable = ({planOffers, selectedPlan, setSelectedPlan, frequency}: IPaymentTable) => {
  const [highestDiscount, setHighestDiscount] = React.useState<number>(30);

  const getFilteredRows = (rowPlans: (IPlanMatrix | null)[]) => {
    return rowPlans.filter((plan) => {
      if (!plan) return true;
      const discount = multiply(plan.discount_fraction, 100);
      return discount <= 25 || discount === highestDiscount;
    });
  };

  if (!planOffers) return null;

  const filteredDiscounts = DISCOUNT_LABELS.filter((discount) => discount <= 30);
  const columnCount = filteredDiscounts.length + 1;

  return (
    <Container
      sx={{
        display: "grid",
        gridTemplateColumns: `60px repeat(${columnCount - 1}, 84px)`,
        gap: 0,
      }}
    >
      <Box sx={{height: "40px"}} />
      {filteredDiscounts.map((discount, index) => (
        <Container key={index} sx={{TableCellStyle}}>
          {discount < 30 ? (
            <Text variant="inter500" size={12} color={Colors.grey._600} sx={{whiteSpace: "nowrap"}}>
              -{discount}%
            </Text>
          ) : (
            <FormControl variant="outlined" sx={{width: "75px"}}>
              <Select
                variant="standard"
                value={highestDiscount}
                onChange={(event: any) => setHighestDiscount(event.target.value)}
                sx={{
                  height: "30px",
                  borderRadius: "8px",
                  "&:before": {borderBottom: "none"},
                  "&:after": {borderBottom: "none"},
                  "&:hover:not(.Mui-disabled):before": {borderBottom: "none"},
                }}
              >
                {DISCOUNT_LABELS.filter((discountOption) => discountOption >= 30).map((discountOption) => (
                  <MenuItem key={discountOption} value={discountOption}>
                    <Text
                      variant="inter500"
                      size={12}
                      color={Colors.grey._600}
                      sx={{whiteSpace: "nowrap", margin: "3px 0px 0px 10px"}}
                    >
                      -{discountOption}%
                    </Text>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Container>
      ))}
      {planOffers.map((rowPlans, rowIndex) => {
        const filteredRows = getFilteredRows(rowPlans);
        return (
          <React.Fragment key={rowIndex}>
            <Container sx={{...TableCellStyle, gridRowStart: rowIndex + 2}}>
              <Text variant="inter500" size={12} color={Colors.grey._600} sx={{whiteSpace: "nowrap"}}>
                {getActualInstallments(NUMBER_OF_INSTALLMENTS_LABELS[rowIndex], frequency)} inst
              </Text>
            </Container>
            {filteredRows.map((plan, colIndex) => (
              <Container
                key={colIndex}
                sx={{...TableCellStyle, gridColumnStart: colIndex + 2, gridRowStart: rowIndex + 2}}
              >
                {plan === null ? null : (
                  <PlanButton
                    plan={plan}
                    setSelectedPlan={setSelectedPlan}
                    isSelected={
                      selectedPlan?.installment_size === plan.installment_size &&
                      selectedPlan?.number_of_installments === plan.number_of_installments
                    }
                  />
                )}
              </Container>
            ))}
          </React.Fragment>
        );
      })}
    </Container>
  );
};
